// @ts-check
import React, {
  useEffect,
  useState,
  forwardRef,
  useImperativeHandle,
} from 'react';
import { formatFinancial } from '../utils/formatters';
import { TextField, InputAdornment } from '@mui/material';

/**
 * @typedef {Object} ValidatedTextFieldProps
 * @property {string} [label]
 * @property {((value: any) => string | false) | null} [validator]
 * @property {(isValid: boolean, value: any) => void} onChange
 * @property {boolean} [valueRequired]
 * @property {string | number | null} [value]
 * @property {string} [startAdornment]
 * @property {string | null} [errorOverride]
 * @property {boolean} [number]
 * @property {boolean} [disabled]
 * @property {boolean} [financial]
 * @property {'outlined' | 'filled' | 'standard'} [variant]
 * @property {'small' | 'medium'} [size]
 * @property {boolean} [focus]
 * @property {(e: React.FocusEvent<HTMLInputElement | HTMLTextAreaElement>) => void} [onFocus]
 * @property {(e: React.FocusEvent<HTMLInputElement | HTMLTextAreaElement>) => void} [onBlur]
 */

/**
 * @type {React.ForwardRefRenderFunction<HTMLInputElement | HTMLTextAreaElement, ValidatedTextFieldProps>}
 */
const ValidatedTextField = forwardRef(
  (
    {
      label,
      validator,
      onChange,
      valueRequired = false,
      value,
      startAdornment = '',
      errorOverride = null,
      number = false,
      disabled = false,
      financial = false,
      variant = 'outlined',
      size = 'small',
      focus = false,
      onFocus: externalOnFocus,
      onBlur: externalOnBlur,
    },
    ref
  ) => {
    const [helperText, setHelperText] = useState('');
    /** @type {[string | number | null, (value: string | number | null) => void]} */
    const [overrideValue, setOverrideValue] = useState(
      /** @type {string | number | null} */ ('')
    );
    const [focused, setFocused] = useState(false);
    const inputRef = React.useRef(null);

    // Expose input ref to parent
    useImperativeHandle(ref, () => inputRef.current);

    // Only sync display from value when not focused, so partial input (e.g. "1.", "1.0") is not overwritten by parsed number
    useEffect(() => {
      if (!focused) {
        if (financial && value && value !== 0) {
          setOverrideValue(formatFinancial(value));
        } else {
          setOverrideValue(value ?? '');
        }
      }
    }, [value, financial, focused]);

    const handleChange = (e) => {
      let rawValue = e.target.value;
      let newValue = rawValue;
      let helperText = '';

      // For number/financial fields, strip commas before validation and onChange
      if (number || financial) {
        // Remove commas and trim whitespace
        const cleanedValue = rawValue.replace(/,/g, '').trim();

        // Allow empty, minus sign, or decimal point for partial input
        if (
          cleanedValue === '' ||
          cleanedValue === '-' ||
          cleanedValue === '.'
        ) {
          newValue = cleanedValue;
        } else if (!isNaN(cleanedValue)) {
          // Valid number - keep as string while typing decimals so "1.0", "1.00" don't collapse to 1
          if (
            cleanedValue.endsWith('.') ||
            ((number || financial) && cleanedValue.includes('.'))
          ) {
            newValue = cleanedValue;
          } else {
            const parsed = parseFloat(cleanedValue);
            newValue = isNaN(parsed) ? cleanedValue : parsed;
          }
        } else {
          // Invalid - keep original for validation error
          newValue = cleanedValue;
        }
      }

      if (validator !== null) {
        if (valueRequired) {
          const validationResult = validator(newValue);
          helperText = validationResult === false ? '' : validationResult;
        } else if (rawValue !== '') {
          const validationResult = validator(newValue);
          helperText = validationResult === false ? '' : validationResult;
        }
      }
      setOverrideValue(newValue);
      setHelperText(helperText);
      onChange(!helperText, newValue);
    };

    const handleFocus = (e) => {
      setFocused(true);
      if (financial) {
        setOverrideValue(value ?? '');
      }
      if (externalOnFocus) {
        externalOnFocus(e);
      }
    };

    const handleBlur = (e) => {
      setFocused(false);
      if (externalOnBlur) {
        externalOnBlur(e);
      }
    };

    return (
      <TextField
        label={label ? label : ''}
        size={size}
        inputRef={(input) => {
          inputRef.current = input;
          if (focus && input) {
            input.focus();
          }
        }}
        fullWidth
        variant={variant}
        multiline
        required={valueRequired}
        maxRows={4}
        onFocus={handleFocus}
        onBlur={handleBlur}
        disabled={disabled}
        inputProps={{ style: { textAlign: number ? 'right' : 'left' } }}
        InputProps={
          startAdornment
            ? {
                startAdornment: (
                  <InputAdornment position="start">
                    {startAdornment}
                  </InputAdornment>
                ),
              }
            : {}
        }
        error={!!helperText || !!errorOverride}
        helperText={helperText || errorOverride}
        onChange={handleChange}
        value={
          /** @type {any} */ (overrideValue) === 0 ||
          overrideValue === null ||
          overrideValue === ''
            ? ''
            : String(overrideValue)
        }
        placeholder={value === 0 ? '0' : undefined}
      />
    );
  }
);

ValidatedTextField.displayName = 'ValidatedTextField';

export default ValidatedTextField;
